import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  CreditCard,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { CartDrawer } from "./CartDrawer";
import { ProductCard } from "./ProductCard";
import { CustomOrderDialog } from "./CustomOrderDialog";
import { Button } from "./ui/button";
import { CATEGORIES, getCategory } from "../data/categories";
import { getProductsByCategory } from "../data/products";

const TRUST = [
  { icon: Truck, text: "Envío 100% garantizado a todo el país" },
  { icon: ShieldCheck, text: "Impreso en MDF de alta calidad" },
  { icon: CreditCard, text: "Pago seguro con Bold o Sistecrédito" },
];

export const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = getCategory(slug);
  const [customOpen, setCustomOpen] = useState(false);

  if (!category) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
        <Navbar />
        <main className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl text-slate-900 mb-4">
            Esta categoría no existe
          </h1>
          <p className="text-slate-600 mb-8">
            Es posible que el enlace haya cambiado.
          </p>
          <Button
            onClick={() => navigate("/categorias")}
            data-testid="category-back-categories"
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
          >
            Ver todas las categorías
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const items = getProductsByCategory(slug);

  const navItemClass = (isActive, variant) => {
    const base =
      variant === "chip"
        ? "shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-300"
        : "block w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium border transition-all duration-300";
    return `${base} ${
      isActive
        ? "bg-[#E85A3C] text-white border-[#E85A3C] shadow-md shadow-[#E85A3C]/25"
        : "bg-white text-slate-700 border-slate-200 hover:border-[#E85A3C]/50 hover:text-[#E85A3C]"
    }`;
  };

  const customCard = (
    <button
      type="button"
      onClick={() => setCustomOpen(true)}
      data-testid="category-custom-card"
      className="group flex flex-col items-center justify-center text-center gap-3 rounded-2xl border-2 border-dashed border-[#E85A3C]/40 bg-[#E85A3C]/[0.03] hover:bg-[#E85A3C]/[0.07] hover:border-[#E85A3C]/70 transition-all duration-300 p-8 min-h-[320px]"
    >
      <span className="h-12 w-12 rounded-full bg-[#E85A3C]/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
        <Sparkles className="h-6 w-6 text-[#E85A3C]" strokeWidth={1.75} />
      </span>
      <span className="font-serif text-lg text-slate-900">
        ¿No encuentras el tuyo?
      </span>
      <span className="text-sm text-slate-600 max-w-[220px]">
        Envíanos tu foto o idea y la convertimos en un cuadro único para ti.
      </span>
      <span className="mt-1 inline-flex items-center bg-[#E85A3C] group-hover:bg-[#D3492D] text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-lg shadow-[#E85A3C]/30 transition-colors">
        Pídelo personalizado
      </span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      <Navbar />
      <CartDrawer />

      <main
        data-testid="category-page"
        className="pt-24 md:pt-28 pb-16 md:pb-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/categorias"
            data-testid="category-back-link"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            Todas las categorías
          </Link>

          {/* Mobile: chips horizontales */}
          <div
            data-testid="category-page-chips"
            className="md:hidden flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 mb-4"
          >
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/categoria/${c.slug}`}
                data-testid={`category-page-chip-${c.slug}`}
                className={navItemClass(c.slug === slug, "chip")}
              >
                {c.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[250px_1fr] gap-8 lg:gap-12 items-start">
            {/* Sidebar izquierdo (desktop) */}
            <nav
              data-testid="category-page-nav"
              aria-label="Categorías"
              className="hidden md:block sticky top-24 space-y-1.5"
            >
              <h2 className="text-xs font-medium uppercase tracking-wider text-slate-500 px-1 mb-3">
                Categorías
              </h2>
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/categoria/${c.slug}`}
                  data-testid={`category-page-nav-item-${c.slug}`}
                  aria-current={c.slug === slug ? "page" : undefined}
                  className={navItemClass(c.slug === slug, "list")}
                >
                  {c.name}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => setCustomOpen(true)}
                data-testid="category-page-nav-custom"
                className="mt-4 w-full text-left px-4 py-3 rounded-xl text-sm border border-dashed border-[#E85A3C]/40 text-[#E85A3C] hover:bg-[#E85A3C]/5 transition-colors"
              >
                <Sparkles className="inline h-3.5 w-3.5 mr-1.5" />
                ¿Otra idea? Pídelo personalizado
              </button>
            </nav>

            {/* Contenido de la categoría */}
            <div
              key={slug}
              className="animate-in fade-in duration-500"
            >
              <span className="block text-xs uppercase tracking-[0.22em] text-[#E85A3C] mb-2">
                {category.tagline}
              </span>
              <h1
                data-testid="category-page-heading"
                className="font-serif text-3xl md:text-5xl text-slate-900 leading-[1.05] tracking-tight"
              >
                {category.name}
              </h1>
              <p
                data-testid="category-page-description"
                className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl"
              >
                {category.description}
              </p>

              {/* Trust strip */}
              <div
                data-testid="category-trust-strip"
                className="flex flex-wrap items-center gap-x-8 gap-y-2 border-y border-slate-200 py-3 mt-6 mb-8"
              >
                {TRUST.map(({ icon: Icon, text }) => (
                  <span
                    key={text}
                    className="inline-flex items-center gap-2 text-xs md:text-sm text-slate-600"
                  >
                    <Icon
                      className="h-4 w-4 text-[#E85A3C]"
                      strokeWidth={1.75}
                    />
                    {text}
                  </span>
                ))}
              </div>

              {/* Grid de cuadros */}
              <div
                data-testid="category-page-grid"
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8"
              >
                {items.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
                {customCard}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <CustomOrderDialog open={customOpen} onOpenChange={setCustomOpen} />
    </div>
  );
};
